import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { createGuideRequest } from "@/lib/api";
import { trackGuideDownload } from "@/lib/analytics";

/**
 * GuideDialog — email gate for the branded PDF guides.
 * On success the lead is stored + emailed the guide, and the PDF opens
 * immediately in a new tab so the visitor never waits on email delivery.
 */
export default function GuideDialog({ open, onOpenChange, guide }) {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setLoading(true);
    try {
      await createGuideRequest({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        guide_slug: guide.slug,
        guide_title: guide.title,
      });
      trackGuideDownload({ guide_slug: guide.slug, element: guide.element, company: form.company });
      window.open(guide.pdf, "_blank", "noopener");
      toast.success("Guide on its way", {
        description: "The PDF is opening now, and we've emailed you a copy.",
      });
      setForm({ name: "", email: "", company: "" });
      onOpenChange(false);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="guide-dialog"
        className="bg-[#121315] border border-[#8C7355]/25 text-[#F5F4F0] sm:max-w-lg p-8 md:p-10 max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <p className="text-[#C9A876] text-[10px] tracking-[0.4em] uppercase">Studio Guide · PDF</p>
          <DialogTitle className="font-serif-display font-light tracking-tight text-3xl md:text-4xl text-[#F5F4F0]">
            {guide?.title}
          </DialogTitle>
          <DialogDescription className="text-[#D6D1CA]/55 font-body">
            A designed PDF you can keep, print or send to your builder. Tell us where to send it.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-5 space-y-6" data-testid="guide-form">
          <input
            data-testid="guide-name"
            className="field-underline w-full"
            placeholder="Full name"
            value={form.name}
            onChange={set("name")}
          />
          <input
            data-testid="guide-email"
            type="email"
            className="field-underline w-full"
            placeholder="Email address"
            value={form.email}
            onChange={set("email")}
          />
          <input
            data-testid="guide-company"
            className="field-underline w-full"
            placeholder="Practice or project (optional)"
            value={form.company}
            onChange={set("company")}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="guide-submit"
            className="btn-bronze inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-[11px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
          >
            <Download size={13} strokeWidth={1.8} />
            {loading ? "Sending…" : "Send me the guide"}
          </button>
          <p className="text-[#D6D1CA]/40 text-[10px] tracking-[0.15em] leading-relaxed">
            We'll email the guide and occasionally share new studio guides. No spam, unsubscribe any
            time.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
